import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary, formatINR } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { useCanAdmin } from "@/context/AuthContext";
import { Plus, Trash2, Building2, Zap, Pencil } from "lucide-react";

const empty = { name: "", type: "Bank", products: [], api_enabled: false, active: true, code: "", payout_type: "flat", flat_mode: "percent", flat_value: "", slabs: [], show_on_home: true, payout_bank_id: "" };

export default function Lenders() {
  const canAdmin = useCanAdmin();
  const canManage = canAdmin("lenders");
  const [lenders, setLenders] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [companyBanks, setCompanyBanks] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/lenders").then((r) => setLenders(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products").then((r) => setProductNames(r.data.filter((p) => p.active).map((p) => p.name))).catch(() => {}); }, []);
  useEffect(() => { api.get("/company-banks").then((r) => setCompanyBanks((r.data || []).filter((b) => b.active))).catch(() => {}); }, []);

  const openNew = () => { setForm({ ...empty, products: [...productNames] }); setEditId(null); setShow(true); };
  const openEdit = (l) => { setForm({ ...empty, ...l }); setEditId(l.id); setShow(true); };

  const submit = async (e) => {
    e.preventDefault();
    const num = (v) => (v === "" || v == null ? 0 : Number(v));
    const payload = {
      ...form,
      flat_value: num(form.flat_value),
      slabs: (form.slabs || []).map((s) => {
        const o = {};
        Object.keys(s).forEach((k) => { o[k] = s[k] === "" || s[k] == null ? "" : Number(s[k]); });
        return o;
      }),
    };
    try {
      if (editId) await api.put(`/lenders/${editId}`, payload);
      else await api.post("/lenders", payload);
      toast.success(editId ? "Lender updated" : "Lender onboarded");
      setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Remove this lender?")) return; await api.delete(`/lenders/${id}`); toast.success("Lender removed"); load(); };

  const toggleProduct = (p) => {
    setForm((f) => ({ ...f, products: f.products.includes(p) ? f.products.filter((x) => x !== p) : [...f.products, p] }));
  };

  const addSlab = () => setForm((f) => ({ ...f, slabs: [...(f.slabs || []), f.payout_type === "irr_tenure_matrix" ? { min_irr: "", max_irr: "", min_tenure: "", max_tenure: "", payout_percent: "" } : { min: "", max: "", payout_percent: "" }] }));
  const updSlab = (i, k, v) => setForm((f) => ({ ...f, slabs: f.slabs.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const removeSlab = (i) => setForm((f) => ({ ...f, slabs: f.slabs.filter((_, idx) => idx !== i) }));
  const [slabTest, setSlabTest] = useState("");
  const previewSlab = () => {
    const val = parseFloat(slabTest);
    if (isNaN(val) || !form.slabs?.length) return null;
    const s = form.slabs.find((x) => {
      const mn = Number(x.min) || 0; const mx = x.max === "" || x.max == null ? Infinity : Number(x.max);
      return val >= mn && val <= mx;
    });
    if (!s) return { none: true };
    const pct = Number(s.payout_percent) || 0;
    return { pct, gross: form.payout_type === "volume_slab" ? val * pct / 100 : null };
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Lenders</h1>
          <p className="text-sm text-slate-500 mt-1">Onboard banks & NBFCs and manage their products</p>
        </div>
        {canManage && <button data-testid="add-lender-button" onClick={openNew} className={btnPrimary}><Plus size={16} /> Onboard Lender</button>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {lenders.map((l) => (
          <div key={l.id} data-testid={`lender-card-${l.id}`} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${l.type === "Bank" ? "bg-blue-100 text-blue-600" : "bg-amber-100 text-amber-600"}`}><Building2 size={22} /></div>
                <div>
                  <div className="font-bold text-slate-900 font-heading">{l.name}</div>
                  <div className="text-xs text-slate-400">{l.type}{l.code ? ` · ${l.code}` : ""}{l.payout_type && l.payout_type !== "flat" ? ` · ${l.payout_type === "volume_slab" ? "Volume slab" : "Count slab"}` : ""}</div>
                </div>
              </div>
              {canManage && (
              <div className="flex gap-1">
                <button data-testid={`edit-lender-${l.id}`} onClick={() => openEdit(l)} className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={14} /></button>
                <button data-testid={`delete-lender-${l.id}`} onClick={() => del(l.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
              </div>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {l.products.slice(0, 6).map((p) => <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{p}</span>)}
            </div>
            {l.payout_bank_id && (() => {
              const b = (companyBanks || []).find((x) => x.id === l.payout_bank_id);
              return b ? <div className="mt-2 text-[11px] text-slate-500" data-testid={`lender-payout-bank-${l.id}`}>Payout → <span className="font-medium text-slate-700">{b.bank_name}</span> · {b.account_no} · {b.ifsc}</div> : null;
            })()}
            <div className="flex items-center gap-3 mt-4 text-xs">
              {l.api_enabled && <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><Zap size={12} /> API Connected</span>}
              <span className={l.active ? "text-emerald-600" : "text-slate-400"}>● {l.active ? "Active" : "Inactive"}</span>
            </div>
          </div>
        ))}
      </div>

      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Edit Lender" : "Onboard Lender"} testId="lender-modal">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Lender Name *</label><input data-testid="lender-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Lender Code</label><input data-testid="lender-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} placeholder="e.g. HDFC01" /></div>
            <div><label className={labelCls}>Lender Contact Email</label><input data-testid="lender-email-input" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="ops@lender.com — receives undertaking letters" /></div>
            <div><label className={labelCls}>Type</label>
              <select data-testid="lender-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                <option value="Bank">Bank</option><option value="NBFC">NBFC</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Payout Bank (Arjun Fintech)</label>
              <select data-testid="lender-payout-bank-select" value={form.payout_bank_id || ""} onChange={(e) => setForm({ ...form, payout_bank_id: e.target.value })} className={inputCls}>
                <option value="">— Select company bank —</option>
                {companyBanks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} · A/C {b.account_no} · {b.ifsc}</option>)}
              </select>
              {companyBanks.length === 0 && <p className="text-xs text-amber-600 mt-1">No active company banks. Add one under Settings → Arjun Fintech Banks.</p>}
            </div>
            <div><label className={labelCls}>Payout Structure</label>
              <select data-testid="lender-payout-type-select" value={form.payout_type} onChange={(e) => setForm({ ...form, payout_type: e.target.value })} className={inputCls}>
                <option value="flat">Flat (% or fixed amount)</option>
                <option value="volume_slab">Volume Slab (by disbursed amount)</option>
                <option value="count_slab">Number Slab (by case count)</option>
                <option value="irr_tenure_matrix">IRR/Tenure Matrix</option>
              </select>
            </div>
          </div>
          {form.payout_type === "flat" && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Flat Payout Basis</label>
                <select data-testid="lender-flat-mode-select" value={form.flat_mode} onChange={(e) => setForm({ ...form, flat_mode: e.target.value })} className={inputCls}>
                  <option value="percent">Percentage of disbursed (%)</option>
                  <option value="amount">Fixed amount per case (₹)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{form.flat_mode === "amount" ? "Amount per case (₹)" : "Payout %"}</label>
                <input data-testid="lender-flat-value-input" type="number" step="0.01" value={form.flat_value} onChange={(e) => setForm({ ...form, flat_value: e.target.value === "" ? "" : Number(e.target.value) })} className={inputCls} placeholder={form.flat_mode === "amount" ? "e.g. 5000" : "e.g. 1.5"} />
                <p className="text-xs text-slate-400 mt-1">Leave blank to use per-product commission rules instead.</p>
              </div>
            </div>
          )}
          {(form.payout_type === "volume_slab" || form.payout_type === "count_slab") && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{form.payout_type === "volume_slab" ? "Amount Slabs (₹)" : "Case-count Slabs"} → Payout %</span>
                <button type="button" data-testid="add-slab-button" onClick={addSlab} className="text-xs text-emerald-600 font-medium">+ Add slab</button>
              </div>
              <div className="space-y-2">
                {(form.slabs || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`slab-min-${i}`} type="number" value={s.min} onChange={(e) => updSlab(i, "min", e.target.value === "" ? "" : Number(e.target.value))} placeholder="Min" className={`${inputCls} col-span-4`} />
                    <input data-testid={`slab-max-${i}`} type="number" value={s.max} onChange={(e) => updSlab(i, "max", e.target.value === "" ? "" : Number(e.target.value))} placeholder="Max (blank=∞)" className={`${inputCls} col-span-4`} />
                    <input data-testid={`slab-percent-${i}`} type="number" step="0.01" value={s.payout_percent} onChange={(e) => updSlab(i, "payout_percent", e.target.value === "" ? "" : Number(e.target.value))} placeholder="%" className={`${inputCls} col-span-3`} />
                    <button type="button" onClick={() => removeSlab(i)} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                {(!form.slabs || form.slabs.length === 0) && <p className="text-xs text-slate-400">No slabs yet. Add rows to define payout % per slab.</p>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Live preview — enter a sample {form.payout_type === "volume_slab" ? "disbursed amount (₹)" : "case count"}</label>
                <div className="flex items-center gap-3">
                  <input data-testid="slab-test-input" type="number" value={slabTest} onChange={(e) => setSlabTest(e.target.value)} className={`${inputCls} max-w-[200px]`} placeholder={form.payout_type === "volume_slab" ? "e.g. 5000000" : "e.g. 12"} />
                  {(() => {
                    const p = previewSlab();
                    if (!p) return <span className="text-xs text-slate-400">Enter a value to preview</span>;
                    if (p.none) return <span data-testid="slab-preview" className="text-xs font-medium text-rose-600">No slab matches this value</span>;
                    return <span data-testid="slab-preview" className="text-sm font-medium text-emerald-700">→ {p.pct}% payout{p.gross != null ? ` · ${formatINR(p.gross)} commission` : ""}</span>;
                  })()}
                </div>
              </div>
            </div>
          )}
          {form.payout_type === "irr_tenure_matrix" && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">IRR % × Tenure (months) Slabs → Payout %</span>
                <button type="button" data-testid="add-slab-button" onClick={addSlab} className="text-xs text-emerald-600 font-medium">+ Add slab</button>
              </div>
              <div className="space-y-2">
                {(form.slabs || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`slab-irr-min-${i}`} type="number" step="0.01" value={s.min_irr} onChange={(e) => updSlab(i, "min_irr", e.target.value === "" ? "" : Number(e.target.value))} placeholder="IRR min" className={`${inputCls} col-span-2`} />
                    <input data-testid={`slab-irr-max-${i}`} type="number" step="0.01" value={s.max_irr} onChange={(e) => updSlab(i, "max_irr", e.target.value === "" ? "" : Number(e.target.value))} placeholder="IRR max" className={`${inputCls} col-span-2`} />
                    <input data-testid={`slab-tenure-min-${i}`} type="number" value={s.min_tenure} onChange={(e) => updSlab(i, "min_tenure", e.target.value === "" ? "" : Number(e.target.value))} placeholder="Ten. min" className={`${inputCls} col-span-2`} />
                    <input data-testid={`slab-tenure-max-${i}`} type="number" value={s.max_tenure} onChange={(e) => updSlab(i, "max_tenure", e.target.value === "" ? "" : Number(e.target.value))} placeholder="Ten. max" className={`${inputCls} col-span-2`} />
                    <input data-testid={`slab-percent-${i}`} type="number" step="0.01" value={s.payout_percent} onChange={(e) => updSlab(i, "payout_percent", e.target.value === "" ? "" : Number(e.target.value))} placeholder="%" className={`${inputCls} col-span-3`} />
                    <button type="button" onClick={() => removeSlab(i)} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                {(!form.slabs || form.slabs.length === 0) && <p className="text-xs text-slate-400">No slabs yet. First matching row wins; blank min/max is open-ended.</p>}
              </div>
            </div>
          )}

          <div>
            <label className={labelCls}>Products Offered</label>
            <div className="flex flex-wrap gap-2">
              {productNames.map((p) => (
                <button type="button" key={p} onClick={() => toggleProduct(p)} data-testid={`product-toggle-${p.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.products.includes(p) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"}`}>{p}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="lender-api-checkbox" type="checkbox" checked={form.api_enabled} onChange={(e) => setForm({ ...form, api_enabled: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> API Integration Enabled</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Active</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="lender-show-home-checkbox" type="checkbox" checked={form.show_on_home !== false} onChange={(e) => setForm({ ...form, show_on_home: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Show on home page</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="submit-lender-button" type="submit" className={btnPrimary}>{editId ? "Save" : "Onboard"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
